import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { motion } from "framer-motion";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  Clock, 
  Globe, 
  MessageSquare 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { cn } from "@/lib/index";

const contactFormSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  email: z.string().email("Ingrese un correo electrónico válido"),
  company: z.string().min(2, "El nombre de la empresa es requerido"),
  subject: z.string().min(5, "El asunto es demasiado corto"),
  message: z.string().min(10, "El mensaje debe tener al menos 10 caracteres"),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export default function Contact() {
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    console.log("Form submitted:", data);
    // Simular envío
    await new Promise((resolve) => setTimeout(resolve, 1500));
    toast.success("Mensaje enviado con éxito. Nos pondremos en contacto pronto.");
    form.reset();
  };

  const contactInfo = [
    {
      icon: <Mail className="w-5 h-5" />,
      label: "Correo Electrónico",
      value: "hola@prymadigital.com",
      link: "mailto:hola@prymadigital.com",
    },
    {
      icon: <Phone className="w-5 h-5" />,
      label: "WhatsApp",
      value: "+56 9 4976 4444",
      link: "https://wa.me/56949764444",
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      label: "Oficina Central",
      value: "Badajoz 100, oficina 1014, piso 10, Las Condes, Santiago, Chile",
      link: "#",
    },
    {
      icon: <Clock className="w-5 h-5" />,
      label: "Horario de Atención",
      value: "Lunes - Viernes: 09:00 - 18:00 (CLT)",
      link: null,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--primary)_0%,_transparent_70%)]" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.smooth}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tighter">
              Diseñemos el <span className="text-primary">futuro</span> juntos.
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed">
              ¿Listo para transformar su negocio con IA y automatización? 
              Nuestro equipo de expertos está listo para escuchar sus desafíos y proponer soluciones de alto impacto.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Form Column */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="bg-card border border-border p-8 md:p-12 rounded-3xl shadow-sm"
          >
            <motion.div variants={fadeInUp} className="mb-8">
              <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <MessageSquare className="text-primary w-8 h-8" />
                Envíenos un mensaje
              </h2>
              <p className="text-muted-foreground">
                Complete el formulario y nos pondremos en contacto en menos de 24 horas.
              </p>
            </motion.div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre completo</FormLabel>
                        <FormControl>
                          <Input placeholder="Juan Pérez" className="bg-muted/50" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Correo electrónico</FormLabel>
                        <FormControl>
                          <Input placeholder="juan@empresa.com" className="bg-muted/50" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Empresa</FormLabel>
                        <FormControl>
                          <Input placeholder="Pryma Tech Ltd" className="bg-muted/50" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Asunto</FormLabel>
                        <FormControl>
                          <Input placeholder="Implementación de Agentes IA" className="bg-muted/50" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mensaje</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Cuéntenos sobre su proyecto o necesidad de automatización..."
                          className="min-h-[150px] bg-muted/50"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit" 
                  className="w-full h-14 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? (
                    "Enviando..."
                  ) : (
                    <>
                      Enviar Solicitud
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </motion.div>

          {/* Info Column */}
          <div className="space-y-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid grid-cols-1 sm:grid-cols-2 gap-8"
            >
              {contactInfo.map((info, idx) => (
                <motion.div
                  key={idx}
                  variants={staggerItem}
                  className="group p-6 rounded-2xl border border-border bg-card hover:border-primary transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
                    {info.icon}
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{info.label}</h3>
                  {info.link ? (
                    <a 
                      href={info.link} 
                      className="text-muted-foreground hover:text-primary transition-colors text-sm break-all"
                    >
                      {info.value}
                    </a>
                  ) : (
                    <span className="text-muted-foreground text-sm">{info.value}</span>
                  )}
                </motion.div>
              ))}
            </motion.div>

            {/* Visual Map Placeholder */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative h-[400px] rounded-3xl overflow-hidden bg-foreground shadow-2xl"
            >
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?q=80&w=1000')] bg-cover bg-center grayscale opacity-30" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground via-transparent to-transparent" />
              
              <div className="absolute bottom-8 left-8 right-8 p-6 bg-background/10 backdrop-blur-md border border-white/10 rounded-2xl">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary rounded-full">
                    <Globe className="text-primary-foreground w-6 h-6 animate-pulse" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">Presencia Global</h4>
                    <p className="text-white/60 text-sm">Operamos remotamente para todo el mundo con bases en Madrid y Ciudad de México.</p>
                  </div>
                </div>
              </div>

              {/* Stylized UI markers on the map placeholder */}
              <div className="absolute top-1/4 left-1/3 w-4 h-4 bg-primary rounded-full ring-4 ring-primary/20 animate-ping" />
              <div className="absolute top-1/2 right-1/4 w-3 h-3 bg-primary/60 rounded-full" />
              <div className="absolute bottom-1/3 left-1/2 w-2 h-2 bg-primary/40 rounded-full" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Callout */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">¿Prefiere una reunión directa?</h2>
          <p className="text-muted-foreground mb-10 max-w-xl mx-auto">
            Agende una consultoría gratuita de 15 minutos con nuestros especialistas técnicos para diagnosticar sus necesidades de automatización.
          </p>
          <Button 
            variant="outline" 
            className="border-primary text-foreground hover:bg-primary hover:text-primary-foreground transition-all px-8 h-12"
          >
            Agendar en Calendly
          </Button>
        </div>
      </section>
    </div>
  );
}
